import { CommonModule } from '@angular/common';
import {
  Component,
  HostBinding,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SpinnerComponent } from '../spinner/spinner.component';

export enum ButtonColor {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
  GHOST = 'ghost',
  OUTLINE = 'outline',
  OUTLINE_GREY = 'outline-grey',
  GREY = 'grey',
  ERROR = 'error',
  BLACK = 'black',
}

@Component({
  selector: 'button[hcButton]',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [MatTooltipModule, MatIconModule, CommonModule, SpinnerComponent],
})
export class ButtonComponent {
  @Input() color: ButtonColor = ButtonColor.PRIMARY;
  @Input() dataTestId: string = '';
  @Input() leftIcon: string = '';
  @Input() rightIcon: string = '';
  @Input() hidden: boolean = false;
  @Input() tooltip: string = '';
  @Input() minWidth: number | undefined = undefined;
  @Input() loading = false;
  @HostBinding('class.hc-button')
  _ctButton = true;

  private _compact: boolean = false;

  @Input()
  set compact(value: boolean) {
    this._compact = value !== null && `${value}` !== 'false';
  }

  private _condensed: boolean = false;

  @Input()
  set condensed(value: boolean) {
    this._condensed = value !== null && `${value}` !== 'false';
  }

  private _fullWidth: boolean = false;

  @Input()
  set fullWidth(value: boolean) {
    this._fullWidth = value !== null && `${value}` !== 'false';
  }

  private _iconButton: boolean = false;

  @Input() set iconButton(value: boolean) {
    this._iconButton = value !== null && `${value}` !== 'false';
  }

  private _smallIcon: boolean = false;

  @Input() set smallIcon(value: boolean) {
    this._smallIcon = value !== null && `${value}` !== 'false';
  }

  @HostBinding('class.hc-button--small-icon')
  get smallIconInButton(): boolean {
    return this._smallIcon;
  }

  @HostBinding('class.hc-button--primary')
  get primary(): boolean {
    return this.color === ButtonColor.PRIMARY;
  }

  @HostBinding('class.hc-button--secondary')
  get secondary(): boolean {
    return this.color === ButtonColor.SECONDARY;
  }

  @HostBinding('class.hc-button--outline')
  get outline(): boolean {
    return this.color === ButtonColor.OUTLINE;
  }

  @HostBinding('class.hc-button--outline-grey')
  get outlineGrey(): boolean {
    return this.color === ButtonColor.OUTLINE_GREY;
  }

  @HostBinding('class.hc-button--grey')
  get grey(): boolean {
    return this.color === ButtonColor.GREY;
  }

  @HostBinding('class.hc-button--tertiary')
  get tertiary(): boolean {
    return this.color === ButtonColor.TERTIARY;
  }

  @HostBinding('class.hc-button--ghost')
  get ghost(): boolean {
    return this.color === ButtonColor.GHOST || this.loading;
  }

  @HostBinding('class.hc-button--error')
  get error(): boolean {
    return this.color === ButtonColor.ERROR;
  }

  @HostBinding('class.hc-button--black')
  get black(): boolean {
    return this.color === ButtonColor.BLACK;
  }

  @HostBinding('class.hc-button--compact')
  get isCompact(): boolean {
    return this._compact;
  }

  @HostBinding('class.hc-button--icon-button')
  get isIconButton(): boolean {
    return this._iconButton;
  }

  @HostBinding('class.hc-button--compact-icon-button')
  get isCompactIconButton(): boolean {
    return this.isIconButton && this.isCompact;
  }

  @HostBinding('class.hc-button--condensed')
  get isCondensed(): boolean {
    return this._condensed;
  }

  @HostBinding('class.hc-button--full-width')
  get isFullWidth(): boolean {
    return this._fullWidth;
  }
}
